// @flow strict

import { locale } from '../Global'
import {
  getFieldsFromNode,
  getAssetURLFromSectionFields,
  type ContentfulNode,
  getLocalisedFieldsFromNode,
  getLocalisedFieldsFromField,
  getAssetURLFromNodeField,
} from '@mondough/contentful'
import { indexName } from '@mondough/contentful/src/richTextDecorators'
import { type HeroSectionProps } from '../components/HeroSection'
import { type TextAndImageProps } from '../components/TextAndImageSection'
import { type TriptychSectionProps } from '../components/TriptychSection'
import { type EmailSignUpFormProps } from '../components/EmailSignUpForm'
import { type MarkdownSectionProps } from '../components/MarkdownSection'
import { type CTAProps } from '../components/CTASection'
import { type SubtitleSectionProps } from '../components/SubtitleSection'
import { type BannerProps } from '../components/Banner'
import { type DisclosureGroupProps } from '../components/DisclosureGroup'

// Determines whether a section should be reversed or not.
// If reverse is manually set in the flags JSON in contentful, then we use that,
// otherwise we just base it on whether the index is odd/even.
type Flags = { [k: string]: string | number | boolean }
const getReverseValue = (flags: Flags = {}, index: number): boolean => {
  let reverse: boolean = false
  if (flags && typeof flags.reverse === 'boolean') {
    reverse = flags.reverse
  } else if (index) {
    reverse = index % 2 === 0
  }

  return reverse
}
// We are using the Prop types from the default components because those are the canonical types and should in theory not change in different themes.
export const getHeroSectionFieldsFromContentful = (
  node: ContentfulNode,
): HeroSectionProps => {
  const allSectionFields = getFieldsFromNode(node)
  const localisedSectionFields = getLocalisedFieldsFromNode(node, locale)
  const { pageTitle, pageDescription, heroCTA, flags, heroImage } =
    localisedSectionFields || {}

  const localisedHeroFields =
    heroImage != null
      ? getLocalisedFieldsFromField(heroImage.fields, locale)
      : null

  const imageUrl = getAssetURLFromSectionFields(
    allSectionFields,
    'heroImage',
    locale,
  )
  const mobileUrl = getAssetURLFromSectionFields(
    allSectionFields,
    'mobileHeroImage',
    locale,
  )
  const { cta, href, category, action, label } =
    getLocalisedFieldsFromField(heroCTA && heroCTA.fields, locale) || {}
  return {
    title: pageTitle,
    description: pageDescription,
    imageUrls: { desktop: imageUrl || '', mobile: mobileUrl },
    cta: { href, text: cta, category, action, label },
    flags: flags,
    heroDescription:
      localisedHeroFields != null ? localisedHeroFields.description : undefined,
  }
}

export const getTextAndImageSectionFieldsFromContentful = (
  node: ContentfulNode,
): TextAndImageProps => {
  const index = node[indexName]
  const { header, textCopy, image, callToAction, flags } =
    getLocalisedFieldsFromNode(node, locale) || {}

  // This allows a reverse flag to be manually set in Contentful, whilst falling
  // back to an automatically generated one.
  const shouldReverse = (index, flags): boolean => {
    if (flags && flags.reverse !== undefined) {
      return flags.reverse
    }

    if (index !== undefined) {
      return index % 2 === 0
    }

    return false
  }

  const reverse = shouldReverse(index, flags)

  const { description } =
    (image && getLocalisedFieldsFromField(image.fields, locale)) || {}
  const desktop = getAssetURLFromNodeField(node, 'image', locale) || ''
  const { cta, href } =
    getLocalisedFieldsFromField(callToAction && callToAction.fields, locale) ||
    {}

  return {
    title: header,
    description: textCopy,
    imageUrls: { desktop },
    imageAlts: { desktop: description },
    reverse: reverse,
    cta: callToAction && { text: cta, href },
    flags: flags,
  }
}

export const getTriptychSectionFieldsFromContentful = (
  node: ContentfulNode,
): TriptychSectionProps => {
  const index = node[indexName]
  const reverse = index ? index % 2 === 0 : false
  const localisedSectionFields = getLocalisedFieldsFromNode(node, locale)
  const { title1, title2, title3, text1, text2, text3, direction, flags } =
    localisedSectionFields || {}
  const icon1 = getAssetURLFromNodeField(node, 'icon1', locale) || ''
  const icon2 = getAssetURLFromNodeField(node, 'icon2', locale) || ''
  const icon3 = getAssetURLFromNodeField(node, 'icon3', locale) || ''
  const image1 = getAssetURLFromNodeField(node, 'image1', locale) || ''
  const image2 = getAssetURLFromNodeField(node, 'image2', locale) || ''
  const image3 = getAssetURLFromNodeField(node, 'image3', locale) || ''

  return {
    sections: [
      { title: title1, text: text1, iconURL: icon1, imageURL: image1 },
      { title: title2, text: text2, iconURL: icon2, imageURL: image2 },
      { title: title3, text: text3, iconURL: icon3, imageURL: image3 },
    ],
    reverse,
    direction,
    flags,
  }
}

export const getEmailSignUpFormFieldsFromContentful = (
  node: ContentfulNode,
): EmailSignUpFormProps => {
  const localisedSectionFields = getLocalisedFieldsFromNode(node, locale)
  const {
    buttonText,
    successMessage,
    titleText,
    waitlistId,
    userConsentText,
    clientId,
    clientSecret,
  } = localisedSectionFields || {}
  return {
    clientId,
    buttonText,
    clientSecret,
    successMessage,
    titleText,
    userConsentText,
    waitlistId,
  }
}

export const getMarkdownSectionFieldsFromContentful = (
  node: ContentfulNode,
): MarkdownSectionProps => {
  const index = node[indexName]
  const reverse = index ? index % 2 === 0 : false
  const localisedSectionFields = getLocalisedFieldsFromNode(node, locale)
  const { markdownContent } = localisedSectionFields || {}

  return { markdownContent, reverse }
}

export const getCTASectionFieldsFromContentful = (
  node: ContentfulNode,
): CTAProps => {
  const CONTENTFUL_TO_COMPONENT_CTA_TYPE = {
    'App Store Badges': 'appStore',
    'Download Button': 'download',
  }
  const localisedSectionFields = getLocalisedFieldsFromNode(node, locale)
  const { ctaType, title, subtitle, flags } = localisedSectionFields || {}

  const reverse = getReverseValue(flags, node[indexName] || 0)

  return {
    ctaType: CONTENTFUL_TO_COMPONENT_CTA_TYPE[ctaType],
    title,
    subtitle,
    reverse,
  }
}

export const getSubtitleSectionFieldsFromContentful = (
  node: ContentfulNode,
): SubtitleSectionProps => {
  const localisedSectionFields = getLocalisedFieldsFromNode(node, locale)
  const { subtitleText, subtitleDescription } = localisedSectionFields || {}
  return { subtitle: subtitleText, blurb: subtitleDescription }
}

export const getBlogRollSectionFieldsFromContentful = (
  node: ContentfulNode,
) => {
  const localisedSectionFields = getLocalisedFieldsFromNode(node, locale)
  const { category, numRows } = localisedSectionFields || {}
  const reverse =
    node[indexName] !== undefined ? node[indexName] % 2 === 0 : false
  const { categoryName } =
    (category && getLocalisedFieldsFromField(category.fields, locale)) || {}
  return {
    categoryName,
    numRows,
    reverse,
  }
}

export const getBannerFieldsFromContentful = (
  node: ContentfulNode,
): BannerProps => {
  const localisedSectionFields = getLocalisedFieldsFromNode(node, locale)
  const { title, link, icon } = localisedSectionFields || {}

  return { title, link, icon }
}

export const getDisclosureGroupFieldsFromContentful = (
  node: ContentfulNode,
): DisclosureGroupProps => {
  const localisedSectionFields = getLocalisedFieldsFromNode(node, locale)
  const { disclosureItems, flags } = localisedSectionFields || {}

  const items = disclosureItems.map(item => {
    const fields = getLocalisedFieldsFromField(item.fields, locale)
    if (fields == null) {
      return { title: null, content: null }
    }
    return {
      title: fields.title,
      content: fields.content,
    }
  })

  return { items, flags }
}
